import {useLocalStorageState} from "@bounce/shared/hooks";
import {useEffect, useMemo} from "react";
import {useLocation} from "react-router-dom";
import {useGetUiSettings} from "~/api/requests";
import {UiSettings} from "@bounce/shared/declarations/server";
import {useAuth} from "@bounce/shared/contexts";
import {usePartnerUi} from "~/contexts/PartnerUiContext";

type UserUiSettings = UiSettings & {
  userId: string;
  time: number;
};

export const useUiSettings = () => {
  const [uiSettings, setUiSettings] = useLocalStorageState<UserUiSettings | undefined>("uiSettings", undefined, {
    encrypted: true,
  });
  const {whiteLabelEnabled} = usePartnerUi();
  const {userId} = useAuth();
  const {search} = useLocation();
  const queryParams = new URLSearchParams(search);
  const urlUserId = queryParams.get("id") || undefined;
  const aggregatedUserId = useMemo<string | undefined>(() => userId || urlUserId, [userId, urlUserId]);

  const isLoading = useMemo<boolean>(
    () => !whiteLabelEnabled && !uiSettings && !!aggregatedUserId,
    [uiSettings, aggregatedUserId]
  );

  const uiSettingsState = useGetUiSettings();

  useEffect(() => {
    const now = new Date().getTime();
    const uiTime = uiSettings?.time || 0;
    const uiUserId = uiSettings?.userId;

    if (aggregatedUserId && (aggregatedUserId !== uiUserId || now - uiTime > 600_000)) {
      (async () => {
        try {
          if (!whiteLabelEnabled) {
            const uiSettings = await uiSettingsState.mutateAsync({user_id: aggregatedUserId});
            setUiSettings({...uiSettings, userId: aggregatedUserId, time: now});
          }
        } catch (e) {}
      })();
    }
  }, [aggregatedUserId]);

  return {uiSettings, isLoading};
};
